import * as React from "react";
import { useNavigate } from "react-router";
import type { CellProps } from "react-table";
import type { CSS } from "~/config/stitches";
import { notificationsMap } from "~/config/user/user-notifications/notificationsMap";
import type {
  UserNotification,
  UserNotifications,
} from "~/config/user/user-notifications/utils/types";
import { useUser } from "~/config/user/UserProvider";
import { SafeArea } from "~/design-system/SafeArea";
import { VirtualList } from "~/screens/App/components/VirtualList";
import { createComponentHook } from "~/types";
import { useUserNotificationsStore } from "../../../../../UserNotificationsScreen.store";
import { EmptyNotifications } from "./components/EmptyNotifications";
import { NotificationListItem } from "./components/NotificationListItem";

// Approximative height of a row for virtualized list and scrolling
const LIST_ITEM_HEIGHT = 82.5;

type NotificationsListMobileProps = {
  notifications?: UserNotifications;
  css?: CSS;
};

const useNotificationsListMobile = createComponentHook(() => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { clearSelection, setClearSelection, setSelectedItems } =
    useUserNotificationsStore((state) => ({
      clearSelection: state.clearSelection,
      setClearSelection: state.setClearSelection,
      setSelectedItems: state.setSelectedItems,
    }));

  const rowClickHandler = (row: UserNotification) => {
    const linkToRedirect = notificationsMap[row.type](
      { user },
      row as never
    ).link;
    navigate(linkToRedirect);
  };

  return {
    actions: {
      rowClickHandler,
      setClearSelection,
      setSelectedItems,
    },
    state: {
      clearSelection,
    },
  };
});

const NotificationsListMobile = (props: NotificationsListMobileProps) => {
  const { actions, state } = useNotificationsListMobile();

  if (!props.notifications || props.notifications.length === 0) {
    return <EmptyNotifications />;
  }

  return (
    <SafeArea top>
      <VirtualList
        rounded
        clearSelection={state.clearSelection}
        setClearSelection={actions.setClearSelection}
        setSelectedItems={actions.setSelectedItems}
        canSelectItems
        css={props.css}
        data={props.notifications}
        item={{
          Cell: (props: CellProps<UserNotification>) => (
            <NotificationListItem data={props.cell.row.original} />
          ),
          accessor: (row: UserNotification) => row.created_at,
          id: "date",
        }}
        estimateSize={() => LIST_ITEM_HEIGHT}
        initialState={{
          sortBy: [{ id: "date" }],
        }}
        rowProps={{
          onClick: actions.rowClickHandler,
        }}
      />
    </SafeArea>
  );
};

export { NotificationsListMobile };
